/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * State Migration Utilities
 *
 * Handles hydration of exit_status on items loaded from the database.
 *
 * The DB column exit_status (added in migration 226) is the canonical field.
 * For rows that predate the migration or have NULL detailed_state, we derive
 * exit_status from whatever signals are present.
 *
 * The old 12-value detailed_state / lifecycle_stage pipeline model is gone.
 * Readiness is computed from named inputs (see itemReadiness.ts).
 */

import type { Item } from '../types/prioritization/lifecycle';
import type { ItemExitStatus, ItemDetailedState } from '../types/prioritization/itemLifecycle';
import type { ItemAltitude } from '../types/prioritization/lifecycle';
import { legacyAltitudeToLevel } from './altitudePrediction';

// ============================================================================
// Exit Status Derivation
// ============================================================================

/**
 * Derive exit_status from a raw item row.
 * Priority order:
 *   1. Use exit_status if already set (new model)
 *   2. Infer from detailed_state (legacy backfill)
 *   3. Default to 'active'
 */
export function deriveExitStatus(item: Record<string, any>): ItemExitStatus {
  // New model — already set (fromDbFormat uses camelCase exitStatus)
  const es = item.exit_status ?? item.exitStatus;
  if (es && ['active', 'deferred', 'archived'].includes(es)) {
    return es as ItemExitStatus;
  }

  // Legacy inference
  const ds = item.detailed_state ?? item.detailedState;
  if (ds === 'DEFERRED') return 'deferred';
  if (ds === 'ARCHIVED') return 'archived';

  return 'active';
}

/**
 * Normalise a raw DB row into an Item with exit_status set.
 * Also maps snake_case DB columns to camelCase app fields.
 */
export function normaliseItem(raw: Record<string, any>): Item & { exit_status: ItemExitStatus } {
  const snakeToApp: Record<string, any> = {};

  if (raw.parent_item_id !== undefined && !raw.parentItemId)   snakeToApp.parentItemId   = raw.parent_item_id;
  if (raw.duplicate_of_id !== undefined && !raw.duplicateOfId) snakeToApp.duplicateOfId  = raw.duplicate_of_id;
  if (raw.split_from_id   !== undefined && !raw.splitFromId)   snakeToApp.splitFromId    = raw.split_from_id;
  if (raw.dedupe_status   !== undefined && !raw.dedupeStatus)  snakeToApp.dedupeStatus   = raw.dedupe_status;
  if (raw.altitude_level  !== undefined && !raw.altitudeLevel) snakeToApp.altitudeLevel  = raw.altitude_level;
  if (raw.altitude_frame  !== undefined && !raw.altitudeFrame) snakeToApp.altitudeFrame  = raw.altitude_frame;

  // Altitude normalization: if altitudeLevel is still unset but the legacy `altitude` string
  // field has a value (e.g. 'initiative', 'epic'), derive altitudeLevel from it so all
  // downstream code can rely on a single field. The legacy string remains intact.
  const resolvedLevel = snakeToApp.altitudeLevel ?? raw.altitudeLevel ?? raw.altitude_level;
  if (!resolvedLevel) {
    const legacyStr = raw.altitude as ItemAltitude | undefined;
    if (legacyStr) {
      const derived = legacyAltitudeToLevel(legacyStr);
      if (derived !== undefined) snakeToApp.altitudeLevel = String(derived);
    }
  }
  if (raw.workspace_id    !== undefined && !raw.workspaceId)   snakeToApp.workspaceId    = raw.workspace_id;
  if (raw.org_unit_id     !== undefined && !raw.orgUnitId)     snakeToApp.orgUnitId      = raw.org_unit_id;
  if (raw.queue_id        !== undefined && !raw.queueId)       snakeToApp.queueId        = raw.queue_id;
  if (raw.item_category   !== undefined && !raw.itemCategory)  snakeToApp.itemCategory   = raw.item_category;

  // Pre-planning / execution fields: fromDbFormat camelCases these; Item type uses snake_case.
  const workType = raw.work_type ?? raw.workType;
  if (workType !== undefined && workType !== null) snakeToApp.work_type = workType;

  const launchType = raw.launch_type ?? raw.launchType;
  if (launchType !== undefined && launchType !== null) snakeToApp.launch_type = launchType;

  const investmentType = raw.investment_type ?? raw.investmentType;
  if (investmentType !== undefined && investmentType !== null) {
    snakeToApp.investment_type = investmentType;
  }

  const externalId = raw.external_id ?? raw.externalId;
  if (externalId !== undefined && externalId !== null) snakeToApp.external_id = externalId;

  const planningMaturity = raw.planning_maturity ?? raw.planningMaturity;
  if (planningMaturity !== undefined && planningMaturity !== null) {
    snakeToApp.planning_maturity = planningMaturity;
  }

  const knowledgeStatus = raw.knowledge_status ?? raw.knowledgeStatus;
  if (knowledgeStatus !== undefined && knowledgeStatus !== null) {
    snakeToApp.knowledge_status = knowledgeStatus;
  }

  const confidenceLevel = raw.confidence_level ?? raw.confidenceLevel;
  if (confidenceLevel !== undefined && confidenceLevel !== null) {
    snakeToApp.confidence_level = confidenceLevel;
  }

  // fromDbFormat() hydrates snake_case DB columns to camelCase (lifecycleStage, lifecycleState).
  // Most UI still reads deprecated snake_case fields (lifecycle_stage). Mirror both so intake
  // filters and dashboards see the same values as the database row.
  const lifecycleStateVal = (raw.lifecycleState ?? raw.lifecycle_state ?? 'item') as Item['lifecycle_state'];
  const lifecycleStageVal = (raw.lifecycleStage ?? raw.lifecycle_stage) as Item['lifecycle_stage'] | undefined;

  const exitStatusVal = deriveExitStatus(raw);

  return {
    ...raw,
    ...snakeToApp,
    lifecycle_state: lifecycleStateVal,
    lifecycle_stage: lifecycleStageVal,
    exit_status: exitStatusVal,
    exitStatus: exitStatusVal,
  } as Item & { exit_status: ItemExitStatus };
}

/**
 * Normalise an array of raw DB rows.
 */
export function normaliseItems(
  items: Record<string, any>[]
): Array<Item & { exit_status: ItemExitStatus }> {
  return items.map(normaliseItem);
}

// ============================================================================
// Zustand Rehydration
// ============================================================================

/**
 * Run normalisation on Zustand rehydrated state.
 * Drop in as onRehydrateStorage handler.
 */
export function onRehydrateItems(state: any): any {
  if (!state?.items) return state;
  return { ...state, items: normaliseItems(state.items) };
}

// ============================================================================
// Report Utilities
// ============================================================================

export interface NormalisationChange {
  id: string;
  title: string;
  derivedExitStatus: ItemExitStatus;
}

export interface NormalisationReport {
  totalItems: number;
  alreadyNormalised: number;
  newlyDerived: number;
  changes: NormalisationChange[];
  timestamp: Date;
}

export function generateNormalisationReport(
  items: Record<string, any>[]
): NormalisationReport {
  const changes: NormalisationChange[] = [];

  for (const item of items) {
    if (!item.exit_status || !['active', 'deferred', 'archived'].includes(item.exit_status)) {
      changes.push({
        id: item.id,
        title: item.title ?? '(untitled)',
        derivedExitStatus: deriveExitStatus(item),
      });
    }
  }

  return {
    totalItems: items.length,
    alreadyNormalised: items.length - changes.length,
    newlyDerived: changes.length,
    changes,
    timestamp: new Date(),
  };
}

// ============================================================================
// Deprecated: Legacy API surface (kept for code that still calls these)
// ============================================================================

/** @deprecated Use deriveExitStatus. */
export function mapLegacyToDetailedState(item: any): ItemDetailedState {
  const es = deriveExitStatus(item);
  if (es === 'deferred') return 'DEFERRED';
  if (es === 'archived') return 'ARCHIVED';
  return 'RAW';
}

/** @deprecated Use normaliseItem. */
export function migrateItem(item: any): any {
  return normaliseItem(item);
}

/** @deprecated Use normaliseItems. */
export function migrateItems(items: any[]): { migrated: any[]; changes: any[] } {
  return { migrated: normaliseItems(items), changes: [] };
}

/** @deprecated */
export function getLifecycleStateFromDetailed(_state: ItemDetailedState): 'item' {
  return 'item';
}

/** @deprecated lifecycle_stage is no longer canonical. */
export function getLifecycleStageFromDetailed(
  _state: ItemDetailedState
): 'intake' | 'shaping' | 'ranking' | 'execution' {
  return 'intake';
}

/** @deprecated Use NormalisationReport. */
export interface MigrationChange {
  id: string;
  title: string;
  previousState: string;
  newDetailedState: ItemDetailedState;
}

/** @deprecated Use NormalisationReport. */
export interface MigrationReport {
  totalItems: number;
  alreadyMigrated: number;
  newlyMigrated: number;
  changes: MigrationChange[];
  timestamp: Date;
}

/** @deprecated Use generateNormalisationReport. */
export function generateMigrationReport(items: any[]): MigrationReport {
  const report = generateNormalisationReport(items);
  return {
    totalItems: report.totalItems,
    alreadyMigrated: report.alreadyNormalised,
    newlyMigrated: report.newlyDerived,
    changes: report.changes.map(c => ({
      id: c.id,
      title: c.title,
      previousState: 'unknown',
      newDetailedState: mapLegacyToDetailedState(c),
    })),
    timestamp: report.timestamp,
  };
}

/** @deprecated */
export function validateMigrationComplete(items: any[]): { complete: boolean; unmigrated: string[] } {
  const unmigrated = items.filter(i => !i.exit_status).map(i => i.id);
  return { complete: unmigrated.length === 0, unmigrated };
}

/** @deprecated */
export function describeMigration(change: MigrationChange): string {
  return `"${change.title}" exit_status derived as ${change.newDetailedState}`;
}

/** @deprecated */
export function createMigrationMiddleware() {
  return (config: any) => (set: any, get: any, api: any) => config(set, get, api);
}

/** @deprecated Use onRehydrateItems. */
export function onRehydrateMigration(state: any): any {
  return onRehydrateItems(state);
}
